import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import { makeStyles } from "@material-ui/core/styles";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import LabelIcon from "@material-ui/icons/Label";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  btn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: 18,
    padding: "4px 10px",
    // Stop the label from wrapping onto two lines when the action bar
    // gets tight (common on mobile next to Accept / undo / bell etc.).
    whiteSpace: "nowrap",
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      fontSize: 12,
      padding: "4px 8px",
    },
  },
  swatch: {
    width: 10,
    height: 10,
    borderRadius: 3,
    flex: "0 0 auto",
  },
}));

const SYSTEM_ICONS = {
  open: <RadioButtonUncheckedIcon fontSize="small" />,
  pending: <HourglassEmptyIcon fontSize="small" />,
  closed: <CheckCircleOutlineIcon fontSize="small" />,
};

/**
 * Quick "Move to" dropdown rendered in the ticket header.
 *
 * - Picking a system section (Open / Pending / Closed) just changes the
 *   ticket status, mirroring the existing Accept / Return / Resolve
 *   buttons but in a single unified picker.
 * - Picking a custom (tag-filter) section moves the ticket into that
 *   tab in true Kanban fashion: the section's tags are added, AND any
 *   tags belonging to *other* custom sections of this company are
 *   stripped, so a ticket lives in exactly one custom column at a
 *   time. Tags that aren't bound to any section (free-form labels) are
 *   preserved.
 *
 * "All" is intentionally omitted — it's a view, not a destination.
 */
const MoveTicketSectionButton = ({ ticket, onTicketUpdate }) => {
  const classes = useStyles();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [sections, setSections] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!anchorEl) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/ticket-sections");
        if (!cancelled) setSections(Array.isArray(data) ? data : []);
      } catch (err) {
        // Quiet — the button just won't show non-system options.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [anchorEl]);

  const close = () => setAnchorEl(null);

  const moveToStatus = async (status) => {
    if (busy || !ticket?.id) return;
    setBusy(true);
    try {
      // Strip every tag that belongs to a custom section so the ticket
      // is no longer "claimed" by any tag-driven column. Otherwise it
      // would still be hidden from the system tabs by the Kanban
      // exclusion in ListTicketsService and the move would look like a
      // no-op. Free-form tags (not part of any section) are preserved.
      const allSectionTagIds = new Set(
        sections
          .filter(
            (s) => s.type !== "system" && Array.isArray(s.tagIds)
          )
          .flatMap((s) => s.tagIds)
      );
      if (allSectionTagIds.size > 0) {
        const existingTagIds = Array.isArray(ticket?.tags)
          ? ticket.tags.map((t) => t.id)
          : [];
        const remaining = existingTagIds.filter(
          (id) => !allSectionTagIds.has(id)
        );
        if (remaining.length !== existingTagIds.length) {
          await api.post(`/tags/sync`, {
            ticketId: ticket.id,
            tags: remaining.map((id) => ({ id })),
          });
        }
      }

      const { data: updated } = await api.put(`/tickets/${ticket.id}`, {
        status,
        userId: status === "pending" ? null : ticket.userId,
      });
      onTicketUpdate && onTicketUpdate(updated);
      toast.success(`Moved to ${status}`);
      if (status !== "open") history.push("/tickets");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
      close();
    }
  };

  // A ticket is considered "currently in" a system tab only when its
  // status matches AND no custom-section tag is hijacking it into a
  // tag-driven column. This makes the disabled state of the dropdown
  // honest — Pending is clickable when the user wants to pull the
  // ticket back from "test2" even if the underlying status is still
  // pending.
  const isClaimedByCustomSection = (() => {
    const sectionTagSet = new Set(
      sections
        .filter((s) => s.type !== "system" && Array.isArray(s.tagIds))
        .flatMap((s) => s.tagIds)
    );
    if (sectionTagSet.size === 0) return false;
    const existing = Array.isArray(ticket?.tags) ? ticket.tags : [];
    return existing.some((t) => sectionTagSet.has(t.id));
  })();

  const moveToCustomSection = async (section) => {
    if (busy || !ticket?.id) return;
    if (!Array.isArray(section.tagIds) || section.tagIds.length === 0) {
      toast.info("This section has no tags configured yet — add some in Manage sections first.");
      close();
      return;
    }
    setBusy(true);
    try {
      // Build the set of tag IDs claimed by all OTHER custom sections
      // of this company. Those tags act as "column membership" markers
      // and must be stripped so the ticket leaves the previous column
      // when it joins this one. Tags that are not part of any section
      // (free-form labels added by agents) are preserved.
      const otherSectionTagIds = new Set(
        sections
          .filter(
            (s) =>
              s.type !== "system" &&
              s.id !== section.id &&
              Array.isArray(s.tagIds)
          )
          .flatMap((s) => s.tagIds)
      );

      const existingTagIds = Array.isArray(ticket?.tags)
        ? ticket.tags.map((t) => t.id)
        : [];

      const preservedExisting = existingTagIds.filter(
        (id) => !otherSectionTagIds.has(id)
      );
      const finalIds = Array.from(
        new Set([...preservedExisting, ...section.tagIds])
      );

      // /tags/sync replaces the ticket's full tag list with whatever we
      // send, so passing finalIds achieves the strip + add atomically.
      const tags = finalIds.map((id) => ({ id }));
      await api.post(`/tags/sync`, { ticketId: ticket.id, tags });

      toast.success(`Moved to "${section.name}"`);
      onTicketUpdate &&
        onTicketUpdate({
          ...ticket,
          tags: finalIds.map((id) => ({ id })),
        });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
      close();
    }
  };

  const customSections = sections.filter(
    (s) => s.type !== "system" && Array.isArray(s.tagIds)
  );

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        className={classes.btn}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
        disabled={busy}
      >
        {i18n.t("moveTicket.button", { defaultValue: "העבר ל..." })}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
        PaperProps={{ style: { minWidth: 220, maxHeight: 360 } }}
      >
        {["open", "pending", "closed"].map((status) => {
          // Only treat the status row as the ticket's current home
          // when it isn't being pulled into a custom column by tags.
          const isCurrentSystemHome =
            ticket?.status === status && !isClaimedByCustomSection;
          return (
            <MenuItem
              key={status}
              disabled={isCurrentSystemHome}
              onClick={() => moveToStatus(status)}
            >
              <ListItemIcon style={{ minWidth: 32 }}>
                {SYSTEM_ICONS[status]}
              </ListItemIcon>
              <ListItemText
                primary={i18n.t(`moveTicket.status.${status}`, {
                  defaultValue: status[0].toUpperCase() + status.slice(1),
                })}
                secondary={isCurrentSystemHome
                  ? i18n.t("moveTicket.current", { defaultValue: "נוכחי" })
                  : null}
              />
            </MenuItem>
          );
        })}

        {customSections.length > 0 && <Divider />}

        {customSections.map((section) => (
          <MenuItem
            key={section.id}
            onClick={() => moveToCustomSection(section)}
          >
            <ListItemIcon style={{ minWidth: 32 }}>
              {section.color ? (
                <span
                  className={classes.swatch}
                  style={{ background: section.color }}
                />
              ) : (
                <LabelIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary={section.name} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MoveTicketSectionButton;
